import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type {
  TournamentPlanCommand,
  TournamentPublishedSchedule,
  TournamentSchedulePlan,
  TournamentSchedulePlanRequest,
  TournamentSchedulePolicyCommand,
  TournamentScheduleReview,
  TournamentScheduleReviewCommand,
  TournamentScheduleState,
} from "@bw/shared/api";
import { useEffect, useState } from "react";

import { TournamentSchedulePlanBoard } from "./tournament-schedule-plan-board";

type Props = {
  tournamentId: string;
  state: TournamentScheduleState;
  plan: TournamentSchedulePlan | null;
  publishedPlans: TournamentSchedulePlan[];
  onUpdatePolicy: (
    command: TournamentSchedulePolicyCommand,
  ) => Promise<unknown>;
  onPreview: (request: TournamentSchedulePlanRequest) => Promise<unknown>;
  onPlanCommand: (command: TournamentPlanCommand) => Promise<unknown>;
  onPublish: (
    command: TournamentPlanCommand,
  ) => Promise<TournamentPublishedSchedule>;
  onRollback: (
    command: TournamentPlanCommand,
  ) => Promise<TournamentPublishedSchedule>;
  onRefresh?: () => Promise<unknown>;
  onReview?: (
    command: TournamentScheduleReviewCommand,
  ) => Promise<TournamentScheduleReview>;
  review?: TournamentScheduleReview | null;
  venueTimeZone?: string;
};

const commandId = () => crypto.randomUUID();
const scheduleSeed = () => Date.now() % 2_147_483_647;

export function TournamentScheduleWorkspace({
  tournamentId,
  state,
  plan,
  publishedPlans,
  onUpdatePolicy,
  onPreview,
  onPlanCommand,
  onPublish,
  onRollback,
  onRefresh,
  onReview,
  review = null,
  venueTimeZone = "UTC",
}: Props) {
  const [duration, setDuration] = useState(
    state.policy.default_duration_minutes,
  );
  const [rest, setRest] = useState(state.policy.minimum_rest_minutes);
  const [publishOpen, setPublishOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] =
    useState<TournamentSchedulePlan | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [retry, setRetry] = useState<(() => Promise<void>) | null>(null);
  const [retryLabel, setRetryLabel] = useState("Retry");
  const canPlan = review?.can_plan ?? true;
  const canPublish = review?.can_publish ?? true;
  useEffect(() => {
    setDuration(state.policy.default_duration_minutes);
    setRest(state.policy.minimum_rest_minutes);
  }, [
    state.tournament_id,
    state.policy.revision,
    state.policy.default_duration_minutes,
    state.policy.minimum_rest_minutes,
  ]);
  const recoverConflict = async (reason: unknown): Promise<boolean> => {
    const isConflict =
      (reason as { status?: number } | undefined)?.status === 409 ||
      /revision|conflict|stale/i.test(
        reason instanceof Error ? reason.message : "",
      );
    const refresh = onRefresh;
    if (!isConflict || !refresh) return false;
    try {
      await refresh();
      setError(
        "Schedule changed. Review the refreshed plan before applying another command.",
      );
      setRetry(null);
    } catch {
      setError(
        "Schedule changed and could not be refreshed. Retry refresh before another command.",
      );
      setRetry(() => async () => {
        setPending(true);
        try {
          await refresh();
          setError(null);
          setRetry(null);
        } catch (refreshReason) {
          setError(
            refreshReason instanceof Error
              ? refreshReason.message
              : "Schedule refresh failed.",
          );
        } finally {
          setPending(false);
        }
      });
      setRetryLabel("Retry refresh");
    }
    return true;
  };

  const savePolicy = async () => {
    setPending(true);
    setError(null);
    try {
      await onUpdatePolicy({
        idempotency_key: commandId(),
        expected_source_revision: state.source_revision,
        policy: {
          default_duration_minutes: duration,
          minimum_rest_minutes: rest,
          draw_policies: state.policy.draw_policies,
          court_requirements: state.policy.court_requirements,
        },
      });
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule policy could not be saved.",
      );
      setRetry(() => savePolicy);
      setRetryLabel("Retry policy save");
    } finally {
      setPending(false);
    }
  };
  const preview = async () => {
    setPending(true);
    setError(null);
    try {
      await onPreview({
        idempotency_key: commandId(),
        expected_source_revision: state.source_revision,
        expected_facility_reservation_revision:
          state.facility_reservation_revision,
        expected_calendar_revision: state.calendar_revision,
        rng_seed: scheduleSeed(),
        planner_version: "lexicographic_v1",
        pins:
          plan?.decisions
            .filter((decision) => decision.pinned)
            .map((decision) => ({
              match: decision.match,
              court_allocation_id: decision.court_allocation_id,
              start_at: decision.start_at,
            })) ?? [],
      });
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule preview could not be generated.",
      );
      setRetry(() => preview);
      setRetryLabel("Retry preview");
    } finally {
      setPending(false);
    }
  };
  const pin = async (decisionId: string, pinned: boolean) => {
    if (!plan) return;
    setPending(true);
    try {
      await onPlanCommand({
        command: pinned ? "unpin_decision" : "pin_decision",
        idempotency_key: commandId(),
        expected_revision: plan.revision,
        decision_id: decisionId,
      });
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule decision could not be pinned.",
      );
      setRetry(() => () => pin(decisionId, pinned));
      setRetryLabel("Retry pin change");
    } finally {
      setPending(false);
    }
  };
  const publish = async () => {
    if (!plan) return;
    setPending(true);
    setError(null);
    setPublishOpen(false);
    try {
      await onPublish({
        command: "publish_plan",
        idempotency_key: commandId(),
        expected_revision: plan.revision,
      });
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule revision changed. Review and retry.",
      );
      setRetry(() => publish);
      setRetryLabel("Retry publish");
      setPublishOpen(false);
    } finally {
      setPending(false);
    }
  };
  const rollback = async () => {
    if (!rollbackTarget || !plan) return;
    setPending(true);
    setError(null);
    try {
      await onRollback({
        command: "rollback_plan",
        idempotency_key: commandId(),
        expected_revision: plan.revision,
        target_plan_id: rollbackTarget.plan_id,
        reason: "organizer_override",
      });
      setRollbackTarget(null);
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule rollback could not be completed.",
      );
      setRetry(() => rollback);
      setRetryLabel("Retry rollback");
    } finally {
      setPending(false);
    }
  };
  const submitReview = async (
    decision: TournamentScheduleReviewCommand["decision"],
    note?: string,
  ) => {
    if (!plan || !onReview) return;
    setPending(true);
    setError(null);
    try {
      await onReview({
        decision,
        idempotency_key: commandId(),
        expected_plan_revision: plan.revision,
        note: note?.trim() || null,
      });
      setRejectOpen(false);
      setRejectNote("");
    } catch (reason) {
      if (await recoverConflict(reason)) return;
      setError(
        reason instanceof Error
          ? reason.message
          : "Schedule review could not be recorded.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Stack component="section" aria-label="Tournament schedule" spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Stack spacing={0.35}>
          <Typography component="h2" variant="h5">
            Schedule planning
          </Typography>
          <Typography color="text.secondary" role="status" variant="body2">
            Build the order of play in {venueTimeZone}. Times, courts and rest
            constraints remain draft until publication.
          </Typography>
        </Stack>
        <Chip
          color={plan?.state === "published" ? "success" : "default"}
          label={
            plan
              ? `${plan.state === "published" ? "Published" : "Draft"} · revision ${plan.revision}`
              : "Not planned"
          }
          variant={plan?.state === "published" ? "filled" : "outlined"}
        />
      </Stack>
      {error ? (
        <Alert
          role="alert"
          severity="error"
          action={
            retry ? (
              <Button size="small" onClick={() => void retry()}>
                {retryLabel}
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      ) : null}
      {canPlan ? (
      <Paper component="section" variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
          >
            <Stack spacing={0.35}>
              <Typography component="h3" variant="h6">
                Planning policy
              </Typography>
              <Typography color="text.secondary" variant="body2">
                These values constrain generated plans. They do not replace
                referee approval for sanctioned events.
              </Typography>
            </Stack>
            <Button
              disabled={pending || rest === 60}
              onClick={() => setRest(60)}
              size="small"
              variant="outlined"
            >
              Use BWF 60-minute rest baseline
            </Button>
          </Stack>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            }}
          >
            <TextField
              helperText="Used unless a draw has a more specific duration."
              label="Default match duration (minutes)"
              slotProps={{ htmlInput: { min: 10, max: 300 } }}
              onChange={(event) => setDuration(Number(event.target.value))}
              type="number"
              value={duration}
            />
            <TextField
              helperText="Minimum interval before the same player can start again."
              label="Minimum rest (minutes)"
              slotProps={{ htmlInput: { min: 0, max: 240 } }}
              onChange={(event) => setRest(Number(event.target.value))}
              type="number"
              value={rest}
            />
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              disabled={pending}
              onClick={() => void savePolicy()}
              variant="outlined"
            >
              Save schedule policy
            </Button>
            <Button
              disabled={pending}
              onClick={() => void preview()}
              variant="contained"
            >
              Preview schedule
            </Button>
          </Stack>
        </Stack>
      </Paper>
      ) : null}
      {plan ? (
        <Stack spacing={2}>
          <Box
            aria-label="Schedule summary"
            sx={{
              display: "grid",
              gap: 1,
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            }}
          >
            {[
              ["Scheduled", plan.metrics.scheduled_count],
              ["Needs work", plan.metrics.unscheduled_count],
              ["Court use", `${plan.metrics.utilization_basis_points / 100}%`],
              ["Maximum wait", `${plan.metrics.max_participant_wait_minutes} min`],
            ].map(([metric, value]) => (
              <Paper key={metric} variant="outlined" sx={{ p: 1.5 }}>
                <Typography color="text.secondary" variant="caption">
                  {metric}
                </Typography>
                <Typography component="p" sx={{ fontWeight: 700 }} variant="h6">
                  {value}
                </Typography>
              </Paper>
            ))}
          </Box>
          <TournamentSchedulePlanBoard
            canEdit={canPlan}
            onPin={pin}
            pending={pending}
            plan={plan}
            venueTimeZone={venueTimeZone}
          />
          {review?.required ? (
            <Alert
              severity={
                review.status === "approved"
                  ? "success"
                  : review.status === "rejected"
                    ? "error"
                    : "info"
              }
            >
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  {review.status === "approved"
                    ? "Referee approved this order of play"
                    : review.status === "rejected"
                      ? "Referee requested schedule changes"
                      : "Referee approval required"}
                </Typography>
                <Typography variant="body2">
                  {review.status === "approved"
                    ? `Approval is bound to draft revision ${review.reviewed_plan_revision}.`
                    : review.note ??
                      "A BWF-profile schedule must be reviewed before the organizer can publish it."}
                </Typography>
                {review.can_review ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    <Button
                      disabled={pending}
                      onClick={() => void submitReview("approved")}
                      size="small"
                      variant="contained"
                    >
                      Approve order of play
                    </Button>
                    <Button
                      disabled={pending}
                      onClick={() => setRejectOpen(true)}
                      size="small"
                      variant="outlined"
                    >
                      Request changes
                    </Button>
                  </Stack>
                ) : review.status === "pending" ? (
                  <Button
                    href={`/tournaments/${encodeURIComponent(tournamentId)}/review-order-of-play`}
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                    variant="outlined"
                  >
                    Open review-only page
                  </Button>
                ) : null}
              </Stack>
            </Alert>
          ) : null}
          {plan.state === "draft" ? (
            <Paper component="section" variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
              >
                <Stack spacing={0.35}>
                  <Typography component="h3" variant="subtitle1">
                    Publication readiness
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {plan.metrics.unscheduled_count
                      ? `${plan.metrics.unscheduled_count} matches are still unscheduled. You can publish a partial order of play after review.`
                      : "Every generated match has a court and time. Review the order of play before publishing."}
                  </Typography>
                </Stack>
                {canPublish ? (
                  <Button
                    disabled={
                      pending ||
                      Boolean(review?.required && review.status !== "approved")
                    }
                    onClick={() => setPublishOpen(true)}
                    variant="contained"
                  >
                    {review?.required && review.status !== "approved"
                      ? "Awaiting referee"
                      : "Review and publish"}
                  </Button>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    The organizer publishes the approved order of play.
                  </Typography>
                )}
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      ) : null}
      {canPublish ? publishedPlans.map((published) => (
        <Button
          key={published.plan_id}
          disabled={pending}
          onClick={() => setRollbackTarget(published)}
          variant="text"
        >
          Rollback to plan {published.revision}
        </Button>
      )) : null}
      <Dialog
        aria-labelledby="reject-schedule-title"
        onClose={() => setRejectOpen(false)}
        open={rejectOpen}
        transitionDuration={0}
      >
        <DialogContent>
          <Stack spacing={2}>
            <Typography id="reject-schedule-title" variant="h6">
              Request schedule changes
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Explain what the organizer must change. The note is saved with
              this exact draft revision.
            </Typography>
            <TextField
              autoFocus
              label="Referee note"
              multiline
              onChange={(event) => setRejectNote(event.target.value)}
              rows={3}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              value={rejectNote}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={pending || !rejectNote.trim()}
            onClick={() => void submitReview("rejected", rejectNote)}
            variant="contained"
          >
            Send change request
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        aria-labelledby="publish-schedule-title"
        onClose={() => setPublishOpen(false)}
        open={publishOpen}
        transitionDuration={0}
      >
        <DialogContent>
          <Typography id="publish-schedule-title" variant="h6">
            Publish schedule
          </Typography>
          <Typography>
            Publishing makes this order of play visible to participants and
            changes court reservations. {plan?.metrics.unscheduled_count ?? 0}{" "}
            matches will remain unscheduled.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button
            disabled={pending}
            onClick={() => void publish()}
            variant="contained"
          >
            Publish schedule
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        aria-labelledby="rollback-schedule-title"
        onClose={() => setRollbackTarget(null)}
        open={rollbackTarget !== null}
        transitionDuration={0}
      >
        <DialogContent>
          <Typography id="rollback-schedule-title" variant="h6">
            Rollback schedule
          </Typography>
          <Typography>Restore the selected prior published plan.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackTarget(null)}>Cancel</Button>
          <Button
            disabled={pending}
            onClick={() => void rollback()}
            variant="contained"
          >
            Rollback schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
